import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismMessage,
  PrismTable,
} from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import HotelBenchmarkInsightCompetitorsTdDesiredCompetitorInfo from 'lumen/components/hotel/benchmark-insight/competitors/td-desired-competitor-info';
import HotelBenchmarkInsightCompetitorsTdName from 'lumen/components/hotel/benchmark-insight/competitors/td-name';
import HotelBenchmarkInsightDesiredCompsetTdDelete from 'lumen/components/hotel/benchmark-insight/desired-compset/td-delete';
import HotelBenchmarkInsightDesiredCompsetTdStars from 'lumen/components/hotel/benchmark-insight/desired-compset/td-stars';
import type { BenchmarkInsightCompetitorsCompetitor } from 'lumen/models';
import type { PrioritizedCategory } from 'lumen/models/benchmark-insight/competitors/proposed-compset';
import { timeout } from 'lumen/utils/waiters';

interface HotelBenchmarkInsightCompetitorsTableCompetitorsSignature<
  T extends BenchmarkInsightCompetitorsCompetitor,
> {
  Args: {
    competitors: T[];
    compsetPrioritizedCategory?: PrioritizedCategory;
    copyRICompset?: () => void;
    deleteCompetitor?: (competitor: T) => void;
    isLoading: boolean;
    isReadOnly?: boolean;
    /** Option to disable the hovering of a competitor row, in case multiple TableCompetitors are shown on the page.
    Otherwise the competitor row will be highlighted in every TableCompetitors at the same time. */
    isRowHoverDisabled?: boolean;
    showDesiredCompetitorAvailability?: boolean;
    showDesiredCompetitorInfoColumn?: boolean;
    showSuggestedCompetitorInfo?: boolean;
    trackDeleteEvent?: boolean;
    trackHoverEvent?: boolean;
    usageAnalyticsEventTrigger?: 'desired-compset' | 'compset-builder';
  };
}

type TableCompetitorsRow = CustomEmberTableRow & {
  competitor: BenchmarkInsightCompetitorsCompetitor;
};
const isTableCompetitorsRow = (
  row: CustomEmberTableRow,
): row is TableCompetitorsRow => {
  return 'competitor' in row;
};

export default class HotelBenchmarkInsightCompetitorsTableCompetitors<
  T extends BenchmarkInsightCompetitorsCompetitor,
> extends Component<
  HotelBenchmarkInsightCompetitorsTableCompetitorsSignature<T>
> {
  @service('benchmark-insight/beni-competitors-service')
  declare benchmarkInsightBeniCompetitorsService: Services['benchmark-insight/beni-competitors-service'];
  @service('benchmark-insight/competitors/desired-compset')
  declare benchmarkInsightCompetitorsDesiredCompset: Services['benchmark-insight/competitors/desired-compset'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];

  @service declare usageAnalytics: Services['usage-analytics'];

  onRowHoverTask = task({ restartable: true }, async (args): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { rowValue } = args;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    if (!rowValue || !isTableCompetitorsRow(rowValue)) {
      this.benchmarkInsightBeniCompetitorsService.handleHover();
      return;
    }

    const { competitor } = rowValue;

    this.benchmarkInsightBeniCompetitorsService.handleHover(competitor);

    // Note: When making changes to `isRowHoverDisabled`, it will impact this tracking event, because this component is used in multiple places.
    // Double check that the hover tracking event is firing in the correct places.
    if (this.args.trackHoverEvent) {
      await timeout('extra-long');

      this.usageAnalytics.track('Compset Wishlist Viewed', {
        eventTrigger: this.args.usageAnalyticsEventTrigger ?? 'desired-compset',
      });
    }
  });

  get columns(): CustomEmberTableColumn[] {
    const {
      compsetPrioritizedCategory,
      showDesiredCompetitorAvailability,
      showDesiredCompetitorInfoColumn,
      isReadOnly,
      trackDeleteEvent,
      usageAnalyticsEventTrigger,
    } = this.args;

    const columns: CustomEmberTableColumn[] = [
      {
        id: 'name',
        name: this.intl.t(
          'benchmark_insight.desired_compset.builder.hotel_name',
        ),
        valuePath: 'competitor',
        tdComponent: HotelBenchmarkInsightCompetitorsTdName,
        thClassNames: 'text-left',
        numberOfCompetitors: this.args.competitors.length,
        compsetPrioritizedCategory,
        showDesiredCompetitorAvailability,
      },
      {
        id: 'stars',
        name: '',
        valuePath: 'competitor.stars',
        tdComponent: HotelBenchmarkInsightDesiredCompsetTdStars,
        maxWidth: 100,
      },
      {
        id: 'distance',
        name: this.intl.t('benchmark_insight.desired_compset.builder.distance'),
        valuePath: 'competitor.distance',
        thClassNames: 'text-left',
        maxWidth: 100,
      },
    ];

    if (showDesiredCompetitorInfoColumn) {
      columns.unshift({
        id: 'desiredCompetitorInfo',
        name: '',
        valuePath: 'competitor.compsetReady',
        tdComponent: HotelBenchmarkInsightCompetitorsTdDesiredCompetitorInfo,
        maxWidth: 50,
      });
    }

    if (!isReadOnly) {
      columns.push({
        id: 'delete',
        name: '',
        valuePath: '',
        tdComponent: HotelBenchmarkInsightDesiredCompsetTdDelete,
        maxWidth: 50,
        trackDeleteEvent,
        usageAnalyticsEventTrigger,
      });
    }

    return columns;
  }

  get rows(): TableCompetitorsRow[] {
    const { showSuggestedCompetitorInfo } = this.args;

    const rows = this.args.competitors.map((competitor) => {
      let isActive = false;

      if (!this.args.isRowHoverDisabled) {
        isActive =
          competitor ===
          this.benchmarkInsightBeniCompetitorsService.hoveredCompetitor;
      }

      const isSuggestedCompetitor =
        showSuggestedCompetitorInfo &&
        this.benchmarkInsightCompetitorsDesiredCompset.isSuggestedCompetitor(
          competitor,
        );

      return {
        competitor,
        isActive,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        deleteCompetitor: () => this.args.deleteCompetitor?.(competitor),
        isSuggestedCompetitor,
      };
    });

    return rows;
  }

  get showButtonCopyRICompset(): boolean {
    return Boolean(
      this.hotelAndMarketService.primaryCompset?.competitors.length,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sorts() {
    return [
      {
        valuePath: 'isSuggestedCompetitor',
        isAscending: true,
      },
    ];
  }

  @action
  onClickCopyRICompset(): void {
    this.args.copyRICompset?.();
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @isLoading={{@isLoading}}
      @nrOfLoadingRows={{10}}
      @onRowHover={{unless
        @isRowHoverDisabled
        this.onRowHoverTask.perform
        undefined
      }}
      @rows={{this.rows}}
      @sorts={{this.sorts}}
      @widthConstraint="eq-container"
      data-test-id="table-competitors"
    >
      <:emptyState>
        <div class="flex h-full w-full items-center justify-center">
          <PrismMessage
            @description={{if
              this.showButtonCopyRICompset
              (t
                "benchmark_insight.desired_compset.builder.empty_state.description"
              )
              (t
                "benchmark_insight.desired_compset.builder.empty_state.description.2"
              )
            }}
            @illustration="100x100_hotel"
            @title={{t
              "benchmark_insight.desired_compset.builder.empty_state.title"
            }}
            data-test-id="table-competitors-empty-state"
          >
            <:primaryAction>
              {{#if this.showButtonCopyRICompset}}
                <PrismButton
                  @buttonType="primary"
                  @label={{t
                    "benchmark_insight.desired_compset.builder.empty_state.action"
                  }}
                  {{on "click" this.onClickCopyRICompset}}
                  data-test-id="table-competitors-empty-state-button"
                />
              {{/if}}
            </:primaryAction>
          </PrismMessage>
        </div>
      </:emptyState>
    </PrismTable>
  </template>
}
